import { CheckCircleIcon, ChevronLeftIcon, ShoppingBagIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import * as React from 'react'
import { useMemo } from 'react'
import { href, Link, useFetcher, useLoaderData, useOutletContext, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import Button from '~/components/webshop-button'
import type { LayoutContext } from '~/routes/_layout'
import { getTenantContext } from '~/services/authz.server'
import { getDeliveryCostsByOrgId, type DeliveryCosts } from '~/services/delivery-costs.server'
import { getOrganizationDetailsByOrgId } from '~/services/org-details.server'
import { useShopContext } from '~/utils/use-shop-context'

type LoaderData = {
  orgDetails: Awaited<ReturnType<typeof getOrganizationDetailsByOrgId>>
  deliveryCosts: DeliveryCosts
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const orgName = data?.orgDetails.companyName ?? 'Webshop'
  const title = `Winkelwagen - ${orgName} webshop`
  const description = `Bekijk en beheer uw winkelwagen bij ${orgName}. Veilig afrekenen en snel geleverd.`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: 'nl_NL' },
    { name: 'robots', content: 'noindex, nofollow' }
  ]
}

export async function loader(args: LoaderFunctionArgs): Promise<LoaderData> {
  const tc = await getTenantContext(args)
  const orgDetails = await getOrganizationDetailsByOrgId(tc.org?.id)
  const deliveryCosts = await getDeliveryCostsByOrgId(tc.org?.id ?? null)
  return { orgDetails, deliveryCosts }
}

export default function Checkout() {
  const {
    snapshot: { items, total_cents, total_qty, applied_discount },
    selectedB2BCustomer,
    isShopOpen,
    canBypassClosedShop
  } = useOutletContext<LayoutContext>()
  const isB2BOrder = selectedB2BCustomer != null
  const { products } = useShopContext()

  // Shop closed for regular customers (admins/owners can bypass)
  const shopClosedForUser = !isShopOpen && !canBypassClosedShop

  const productById = useMemo(() => {
    const m = new Map<string, NonNullable<typeof products>[number]>()
    for (const p of products ?? []) m.set(p.id, p)
    return m
  }, [products])

  const { deliveryCosts } = useLoaderData<typeof loader>()
  const fetcher = useFetcher()
  const busy = fetcher.state !== 'idle'
  const fmt = (c: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(c / 100)

  // === VERZENDKOSTEN LOGICA ===
  const totalUnits = items.reduce((acc, it) => acc + Number(it.qty || 0), 0)

  let shipping_cents = deliveryCosts.large_parcel_cents

  if (totalUnits === 1) {
    const single = items.find(it => (it.qty || 0) > 0)
    if (single) {
      const prod = productById.get(single.product_id)
      if (prod?.small_package === true) {
        shipping_cents = deliveryCosts.small_parcel_cents
      }
    }
  }

  if (total_cents >= deliveryCosts.free_shipping_threshold_cents) {
    shipping_cents = 0
  }

  const remaining_for_free_cents = Math.max(0, deliveryCosts.free_shipping_threshold_cents - total_cents)
  const discount_cents = applied_discount?.discount_cents ?? 0
  const to_pay_cents = Math.max(0, total_cents + shipping_cents - discount_cents)
  const freeShippingProgress = Math.min(100, (total_cents / deliveryCosts.free_shipping_threshold_cents) * 100)

  // Discount code apply/remove
  const applyFetcher = useFetcher<{ success: boolean; error?: string }>()
  const [codeInput, setCodeInput] = React.useState('')
  const [applyError, setApplyError] = React.useState<string | null>(null)
  const applying = applyFetcher.state !== 'idle'

  React.useEffect(() => {
    if (applyFetcher.state === 'idle' && applyFetcher.data) {
      if (applyFetcher.data.success) {
        setApplyError(null)
        setCodeInput('')
      } else if (applyFetcher.data.error) {
        setApplyError(applyFetcher.data.error)
      }
    }
  }, [applyFetcher.state, applyFetcher.data])

  const handleApplyCode = () => {
    const code = codeInput.trim().toUpperCase()
    if (!code) return
    setApplyError(null)
    applyFetcher.submit({ code }, { method: 'POST', action: '/api/checkout/apply-discount-code', encType: 'application/json' })
  }

  const handleRemoveCode = () => {
    applyFetcher.submit(null, { method: 'DELETE', action: '/api/checkout/apply-discount-code' })
  }

  // Empty cart state
  if (!items.length) {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-1">
        <div className="w-full px-4 py-4 lg:px-0">
          <nav className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-medium text-(--ws-link) transition-colors duration-200 hover:text-(--ws-link-hover)">
              <ChevronLeftIcon className="h-4 w-4" />
              Terug naar webshop
            </Link>
          </nav>
          <h1 className="text-title mb-6 text-(--ws-heading)">Winkelwagen</h1>
          <div className="ws-tile-empty">
            <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-(--ws-accent-tint) text-(--ws-accent)">
              <ShoppingBagIcon className="size-8" strokeWidth={1.5} />
            </div>
            <p className="text-lg font-medium text-(--ws-text)">Je winkelwagen is leeg</p>
            <p className="mt-1 mb-6 text-sm text-(--ws-text-muted)">Voeg producten toe om te beginnen met winkelen.</p>
            <Link to="/">
              <Button fullWidth={false}>Bekijk producten</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1">
      <div className="w-full px-4 py-4 lg:px-0">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-medium text-(--ws-link) transition-colors duration-200 hover:text-(--ws-link-hover)">
            <ChevronLeftIcon className="h-4 w-4" />
            Terug naar webshop
          </Link>
        </nav>

        <h1 className="text-title mb-6 text-(--ws-heading)">
          Winkelwagen
          <span className="ml-3 text-base font-normal tracking-normal text-(--ws-text-muted) tabular-nums">
            ({total_qty} {total_qty === 1 ? 'artikel' : 'artikelen'})
          </span>
        </h1>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
          {/* Cart Items */}
          <div className="ws-card divide-y divide-(--ws-border) self-start overflow-hidden">
            {items.map((it, idx) => {
              const lineTotal = it.price_cents * it.qty
              return (
                <div key={`${it.product_id}-${idx}`} className="flex flex-col p-5 sm:flex-row sm:items-center sm:justify-between">
                  <Link to={href('/product/:slug', { slug: it.slug })} className="group flex w-full items-start gap-4 sm:w-auto sm:flex-1">
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.title}
                        className="size-20 flex-shrink-0 rounded-(--ws-radius-inner) border border-(--ws-border) bg-white object-contain transition-colors duration-200 group-hover:border-(--ws-link)"
                      />
                    ) : (
                      <div className="flex size-20 flex-shrink-0 items-center justify-center rounded-(--ws-radius-inner) bg-(--ws-surface-2)">
                        <ShoppingBagIcon className="h-8 w-8 opacity-30" />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-(--ws-text) transition-colors duration-200 group-hover:text-(--ws-link)">{it.title}</div>
                      <div className="mt-1 text-sm text-(--ws-text-muted)">{fmt(it.price_cents)} per stuk</div>
                    </div>
                  </Link>
                  <div className="mt-4 flex flex-col gap-3 sm:mt-0 sm:min-w-[300px] sm:flex-row sm:items-center sm:justify-end">
                    <div className="flex items-center gap-2">
                      <QtySelect productId={it.product_id} qty={it.qty} disabled={busy} />
                      <RemoveButton productId={it.product_id} disabled={busy} />
                    </div>
                    <div className="w-24 text-base font-semibold text-(--ws-text) tabular-nums sm:text-right">{fmt(lineTotal)}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Order Summary */}
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="ws-card shadow-float p-6">
              <h2 className="mb-4 text-lg font-bold text-(--ws-heading)">Overzicht</h2>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-(--ws-text-muted)">
                    Subtotaal
                    <span className="ml-2 rounded-full bg-(--ws-surface-2) px-2 py-0.5 text-xs font-medium text-(--ws-text) tabular-nums">
                      {total_qty}
                    </span>
                  </span>
                  <span className="text-(--ws-text) tabular-nums">{fmt(total_cents)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-(--ws-text-muted)">Verzendkosten</span>
                  <span className={`font-medium tabular-nums ${shipping_cents === 0 ? 'text-(--ws-success)' : 'text-(--ws-text)'}`}>
                    {shipping_cents === 0 ? 'Gratis' : fmt(shipping_cents)}
                  </span>
                </div>

                {/* Free shipping progress */}
                {remaining_for_free_cents > 0 ? (
                  <div className="pt-2">
                    <div className="mb-2 flex justify-between text-xs">
                      <span className="text-(--ws-text-muted)">Nog {fmt(remaining_for_free_cents)} voor gratis verzending</span>
                      <span className="text-(--ws-success) tabular-nums">{Math.round(freeShippingProgress)}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-(--ws-surface-2)">
                      <div
                        className="ease-soft h-full rounded-full bg-(--ws-success) transition-[width] duration-500"
                        style={{ width: `${freeShippingProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 pt-1 text-xs font-medium text-(--ws-success)">
                    <CheckCircleIcon className="h-4 w-4" />
                    Gratis verzending
                  </div>
                )}

                {/* Discount code — consumer only */}
                {!isB2BOrder && (
                  <div className="pt-2">
                    {!applied_discount ? (
                      <div>
                        <div className="flex overflow-hidden rounded-(--ws-radius-inner) border border-(--ws-border) transition-colors focus-within:border-(--ws-link) focus-within:ring-2 focus-within:ring-(--ws-link-tint)">
                          <input
                            type="text"
                            value={codeInput}
                            onChange={e => setCodeInput(e.target.value.toUpperCase())}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleApplyCode()
                              }
                            }}
                            placeholder="Kortingscode"
                            className="min-w-0 flex-1 bg-transparent px-3 py-2 font-mono text-sm text-(--ws-text) uppercase placeholder:text-(--ws-text-muted) focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={handleApplyCode}
                            disabled={applying || !codeInput.trim()}
                            className="inline-flex cursor-pointer items-center gap-2 bg-(--ws-btn-bg) px-3 py-2 text-sm font-medium text-(--ws-btn-text) transition-colors hover:bg-(--ws-btn-bg-hover) disabled:cursor-not-allowed disabled:opacity-50">
                            {applying && <span className="ws-spinner" aria-hidden />}
                            Toepassen
                          </button>
                        </div>
                        {applyError && <p className="mt-1 text-xs text-red-600">{applyError}</p>}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-(--ws-text-muted)">Korting</span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-(--ws-success-tint) py-0.5 pr-1 pl-2 font-mono text-xs font-medium text-(--ws-success)">
                              {applied_discount.code}
                              <button
                                type="button"
                                onClick={handleRemoveCode}
                                disabled={applying}
                                aria-label="Kortingscode verwijderen"
                                className="cursor-pointer rounded-full p-0.5 transition-opacity hover:opacity-70 disabled:opacity-50">
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          </span>
                          <span className="font-medium text-(--ws-success) tabular-nums">−{fmt(applied_discount.discount_cents)}</span>
                        </div>
                        {applied_discount.capped && (
                          <div className="mt-2 rounded-(--ws-radius-inner) border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            Let op: deze kortingscode is {fmt(applied_discount.nominal_cents)} waard, maar je winkelwagen is {fmt(total_cents)}. De
                            korting is gecapt op {fmt(applied_discount.discount_cents)}. Bij bestellen wordt de code volledig verbruikt — het restant
                            van {fmt(applied_discount.nominal_cents - applied_discount.discount_cents)} vervalt.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Total */}
                <div className="mt-4 border-t border-(--ws-border) pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-(--ws-heading)">Totaal</span>
                    <span className="text-xl font-bold text-(--ws-heading) tabular-nums">{fmt(to_pay_cents)}</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="mt-6">
                {shopClosedForUser ? (
                  <div>
                    <Button type="button" disabled>
                      Webshop gesloten
                    </Button>
                    <p className="mt-2 text-center text-sm text-amber-700">Deze webshop is momenteel gesloten voor bestellingen.</p>
                  </div>
                ) : (
                  <Link to={href('/checkout/address')}>
                    <Button type="button">Verder naar bestellen</Button>
                  </Link>
                )}
              </div>

              {/* Clear Cart */}
              <fetcher.Form method="post" action={href('/cart')} className="mt-4 text-center">
                <input type="hidden" name="_intent" value="clear" />
                <button
                  type="submit"
                  disabled={busy}
                  className="cursor-pointer text-xs text-(--ws-text-muted) transition-colors duration-200 hover:text-(--ws-link)">
                  Winkelwagen legen
                </button>
              </fetcher.Form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function QtySelect({ productId, qty, disabled }: { productId: string; qty: number; disabled: boolean }) {
  const fetcher = useFetcher<{ ok: boolean; error?: string }>()
  const [localQty, setLocalQty] = React.useState(qty)
  const [showError, setShowError] = React.useState(false)
  const errorMsg = React.useRef<string | null>(null)

  // Sync localQty with actual cart qty (on successful update or external change)
  React.useEffect(() => {
    setLocalQty(qty)
  }, [qty])

  // Handle error response: revert to original qty and show error
  React.useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && !fetcher.data.ok) {
      setLocalQty(qty) // Revert to the cart's actual qty
      errorMsg.current = fetcher.data.error ?? 'Voorraad onvoldoende'
      setShowError(true)
      // Auto-hide error after 5 seconds
      const timer = setTimeout(() => setShowError(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [fetcher.state, fetcher.data, qty])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQty = Number(e.target.value)
    setLocalQty(newQty)
    setShowError(false)
    fetcher.submit(e.currentTarget.form)
  }

  return (
    <div className="relative">
      <fetcher.Form method="post" action={href('/cart')}>
        <input type="hidden" name="_intent" value="setQty" />
        <input type="hidden" name="product_id" value={productId} />
        <select
          name="qty"
          value={localQty}
          onChange={handleChange}
          disabled={disabled || fetcher.state !== 'idle'}
          className={`h-10 cursor-pointer rounded-(--ws-radius-inner) border bg-white px-2 text-sm tabular-nums disabled:cursor-not-allowed disabled:opacity-50 ${
            showError ? 'border-red-500' : 'border-(--ws-border)'
          }`}
          aria-label="Aantal">
          {[...Array(10)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
          <option value={0}>0 (verwijder)</option>
        </select>
      </fetcher.Form>
      {showError && errorMsg.current && (
        <div className="shadow-card absolute top-full left-0 z-10 mt-1 w-48 rounded-(--ws-radius-inner) border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          {errorMsg.current}
        </div>
      )}
    </div>
  )
}

function RemoveButton({ productId, disabled }: { productId: string; disabled: boolean }) {
  const fetcher = useFetcher()
  return (
    <fetcher.Form method="post" action={href('/cart')}>
      <input type="hidden" name="_intent" value="remove" />
      <input type="hidden" name="product_id" value={productId} />
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex h-10 cursor-pointer items-center gap-1 rounded-(--ws-radius-inner) border border-(--ws-border) bg-white px-3 text-sm font-medium text-red-600 transition-all duration-200 hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Verwijderen"
        title="Verwijderen">
        <TrashIcon className="h-4 w-4" />
        <span className="hidden sm:inline">Verwijder</span>
      </button>
    </fetcher.Form>
  )
}
